"use client";

import { useIsStoreOpen } from "@/hooks/use-is-store-open";
import { useRouter } from "next/navigation";
import {
  Button,
  Divider,
  Drawer,
  Icon,
  Text,
  useToast,
} from "@stone-payments/jade";
import { Product } from "@/actions/schemas/products";
import { DrawerCartQuantity } from "@/components/drawer-cart-quantity";
import { Item, useOrderResumeStore } from "@/stores/order-resume-store";
import { EmptyCart } from "@/app/(store)/_components/empty-cart";
import { share } from "@/app/(store)/_utils/share";
import { Mask } from "@/utils/masks";
import { MessageBuilder } from "@/utils/message-builder";
import { useVitrineStore } from "@/contexts/vitrine-provider";
import { cartAnalitica } from "@/app/(store)/_utils/events";
import { useExperiment } from "@/contexts/analitica";
import { ExperimentsFlags } from "@/services/amplitude/experiment";
import { ClosedStoreCart } from "../closed-store-cart";

type Props = {
  suggestions: Product[];
};

export const CartDrawer = ({ suggestions }: Props) => {
  const { addToast } = useToast();
  const router = useRouter();

  const {
    drawer,
    toggleDrawer,
    items,
    updateItem,
    removeItem,
    clearItems,
    summary,
  } = useOrderResumeStore();

  const store = useVitrineStore((state) => state.store);
  const { isOpen: isStoreOpen, flagOn: isStoreOpenFlagOn } = useIsStoreOpen();
  const preCheckout = useVitrineStore(
    (state) => state.store.vitrine.preCheckout
  );

  const createPaymentLinkExperiment = useExperiment(
    ExperimentsFlags.CreatePaymentLink
  );

  const checkoutExperiment = useExperiment(ExperimentsFlags.Checkout);

  const addQuantity = (item: Item) => {
    updateItem({ ...item, quantity: item.quantity + 1 });
  };

  const subtractQuantity = (item: Item) => {
    const newQuantity = item.quantity - 1;
    if (newQuantity > 0) {
      updateItem({ ...item, quantity: newQuantity });
    } else {
      removeItem(item);
    }
  };

  const onClickProduct = () => toggleDrawer();

  const shareToWhatsapp = () => {
    cartAnalitica.events.action.sendToWhatsapp();

    const message = MessageBuilder.buildOrderResumeMessage({
      items,
      summary,
    });

    share.whatsapp(store.vitrine.whatsAppNumber!, message);

    addToast({
      leading: <Icon color="on-color-high" size="large" use="check" />,
      text: "Pedido enviado! Verifique o WhatsApp da loja para finalizar seu pedido.",
    });

    clearItems();
    toggleDrawer();
  };

  const dispatchCheckoutAction = () => {
    // If has checkout enabled but checkout experiment is off, just send whatsapp message
    const isCheckoutFlagEnabled = checkoutExperiment?.key === "on";
    if (isCheckoutFlagEnabled) {
      router.push(`/${store.identifier}/resumo-do-pedido`);
    } else {
      shareToWhatsapp();
    }
  };

  const dispatchPaymentLinkAction = () => {
    // If has payment link enabled but payment link experiment is off, just send whatsapp message
    const isPaymentLinkFlagEnabled = createPaymentLinkExperiment?.key === "on";
    if (isPaymentLinkFlagEnabled) {
      router.push(`/${store.identifier}/resumo-do-pedido`);
    } else {
      shareToWhatsapp();
    }
  };

  const completeOrder = () => {
    const isCheckout = preCheckout.paymentMethods?.online?.enabled;
    if (isCheckout) {
      dispatchCheckoutAction();
      return;
    }

    const isPaymentLink = preCheckout.paymentMethods?.paymentLink?.enabled;
    if (isPaymentLink) {
      dispatchPaymentLinkAction();
      return;
    }

    // // If is only whatsapp and has delivery or pickup enabled, redirect to order summary page
    const hasDeliveryEnabled = preCheckout.delivery.enabled;
    const hasPickupEnabled = preCheckout.pickup.enabled;

    if (hasDeliveryEnabled || hasPickupEnabled) {
      router.push(`/${store.identifier}/resumo-do-pedido`);
      return;
    }

    // // If is only whatsapp and has no delivery or pickup enabled, just send whatsapp message
    shareToWhatsapp();
  };

  return (
    <Drawer
      open={drawer.isOpen}
      title={
        isStoreOpen ? `Seu carrinho (${summary.itemsCount})` : "Seu carrinho"
      }
      toggle={toggleDrawer}
      background="surface"
      loading={false}
      backdrop
      allowOutsideClick={false}
    >
      {isStoreOpenFlagOn && !isStoreOpen ? (
        <ClosedStoreCart />
      ) : items.length > 0 ? (
        <div className="h-[calc(100vh-68px)] pb-37.5">
          <div className="h-full overflow-y-auto">
            {items.map((item, index) => (
              <div key={item.id} className="first:mt-jade-250">
                <DrawerCartQuantity
                  image={item.image}
                  name={item.name}
                  description={item.description}
                  price={item.price.sale}
                  quantity={item.quantity}
                  onIncrease={() => addQuantity(item)}
                  onDecrease={() => subtractQuantity(item)}
                />

                {index < summary.itemsCount - 1 ? (
                  <div className="mx-jade-250">
                    <Divider />
                  </div>
                ) : null}
              </div>
            ))}
          </div>

          <div className="absolute right-0 bottom-0 left-0 bg-white">
            <Divider />

            <div className="flex flex-col gap-jade-150 p-jade-250">
              <div className="flex justify-between">
                <Text variant="text-medium" color="high" weight="medium">
                  Subtotal:
                </Text>
                <Text variant="heading-small" color="high">
                  {Mask.formatToBrlCurrency(summary.subtotal)}
                </Text>
              </div>

              <Button
                className="w-full"
                variant="primary-solid"
                onClick={completeOrder}
              >
                Continuar
              </Button>

              <Text className="text-center" variant="text-small" color="medium">
                Seu pedido vai ser finalizado no WhatsApp da loja.
              </Text>
            </div>
          </div>
        </div>
      ) : (
        <EmptyCart
          store={store}
          suggestions={suggestions}
          onClickProduct={onClickProduct}
        />
      )}
    </Drawer>
  );
};
